import { useCallback, ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { Subscription, CancellationDetails } from '@localstack/types';
import {
  ButtonWithDialog,
  ContentModalActions,
  ControlledSelect,
  ControlledTextField,
  DeleteForever,
} from '@localstack/ui';
import { CANCELLATION_REASONS } from '@localstack/constants';
import { VALIDATION_RULES } from '@localstack/services';

import { DialogContentText, MenuItem, Box, Typography, Stack } from '@mui/material';

interface SubscriptionCancellationMenuItemProps {
  /** Whether the confirm Modal button should appear in the loading state */
  isLoading: boolean;
  /** The subscription to be cancelled */
  subscription: Optional<Subscription>;
  /** Handler to be called to cancel the subscription */
  onCancelSubscription: (id: string, details: CancellationDetails) => unknown;
}

export const SubscriptionCancellationMenuItem = ({
  isLoading,
  subscription,
  onCancelSubscription,
}: SubscriptionCancellationMenuItemProps): ReactElement => {
  const { control, handleSubmit, formState } = useForm<CancellationDetails>({ mode: 'all' });

  const onSubmit = useCallback(
    async (details, closeDialog) => {
      if (!subscription) return;
      await onCancelSubscription(subscription.id, details);
      closeDialog();
    },
    [subscription],
  );

  return (
    <>
      <ButtonWithDialog
        componentType="MenuItem"
        dialogTitle="Cancel Subscription"
        hideDialogActions
        dialogContent={(closeDialog) => (
          <form onSubmit={handleSubmit((data) => onSubmit(data, closeDialog))}>
            <Stack spacing={2} pt={1}>
              <DialogContentText>
                We&apos;re sorry to see you go! Please provide your feedback to help us improve our services.
              </DialogContentText>
              <Box>
                <ControlledSelect
                  control={control}
                  name="feedback"
                  variant="outlined"
                  label="Cancellation Reason"
                  fullWidth
                  required
                  rules={VALIDATION_RULES.required}
                  options={CANCELLATION_REASONS.map(({ key, text }) => (
                    <MenuItem key={key} value={key}>
                      {text}
                    </MenuItem>
                  ))}
                />
              </Box>
              <Box mt={1}>
                <ControlledTextField
                  control={control}
                  name="comment"
                  variant="outlined"
                  label="Feedback"
                  fullWidth
                  multiline
                  minRows={5}
                />
              </Box>
              <DialogContentText>You are about to cancel the following Subscription</DialogContentText>
              <Typography variant="subtitle1">{subscription?.plan?.name}</Typography>
              <Typography variant="caption" color="error">
                The cancellation will be effective starting with the next billing cycle. Please note that{' '}
                <strong>all API keys, events, and other data</strong> associated with this subscription{' '}
                <strong>will be permanently deleted</strong>!
              </Typography>
            </Stack>
            <Box pt={2}>
              <ContentModalActions
                onClose={closeDialog}
                cancelButtonText="Close"
                confirmButtonText="Cancel Subscription"
                confirmButtonProps={{
                  loading: isLoading,
                  disabled: !formState.isValid,
                  type: 'submit',
                }}
              />
            </Box>
          </form>
        )}
      >
        <DeleteForever fontSize="small" />
        <Typography variant="inherit" sx={{ marginLeft: '0.5em' }}>
          Cancel subscription
        </Typography>
      </ButtonWithDialog>
    </>
  );
};
